import { useEffect, useMemo, useState, type CSSProperties } from 'react'
import { withQty } from '../../utils/lineMath'
import { lineKey } from '../../context/CartContext'
import { formatTRY, kurusToLiraInput, liraToKurus } from '../../utils/money'
import { AmountField } from './AmountField'
import { ChevronLeftIcon, ChevronRightIcon, XIcon } from '../icons'
import { DiscountTender } from './OtherPaymentSheet'
import { PayActions, type PayExtras, type PayPanel } from './PayActions'
import { DebtorPicker } from './DebtorPicker'
import { PAYMENT_LABELS, type DebtorBalance, type Order, type OrderLine, type PaymentMethod } from '../../types'

/** Bir parçanın ödemesine eklenenler: indirim, üstü kalan para, yemek kartı, cari. */
export interface SplitExtras extends PayExtras {
  discountPercent?: number
  /** Cari'ye yazılan pay: kimin hesabına ve açıklama. */
  debtor?: { debtor: DebtorBalance; note: string }
}

interface Props {
  /** The open ticket being split. Live, re-renders as parts are settled. */
  ticket: Order
  discounts: number[]
  onSettlePart: (
    linesTaken: OrderLine[],
    method: PaymentMethod,
    extras: SplitExtras,
    print: boolean,
  ) => void
  /**
   * Tutarla ara ödeme (22 Eki 2026): ürün seçmeden "800'ünü ben veriyorum".
   * Adisyon açık kalıyor, kalan azalıyor; tutar kalanın tamamıysa kapanıyor.
   */
  onSettleAmount: (amountKurus: number, method: PaymentMethod, extras: SplitExtras, print: boolean) => void
  /** Bu adisyondan alınmış ara ödemeler, alınma sırasıyla (tek tek gösterilir). */
  partials?: Order[]
  /**
   * Üstü kalan paranın adı ("Bahşiş"); yalnız bahşiş modülü açıksa verilir,
   * yoksa Üstü Kalsın düğmesi görünmez (ana kasadaki gibi).
   */
  bahsisLabel?: string
  /** Cari modülü açıksa pay bir kişinin hesabına yazılabilir. */
  cariOn?: boolean
  /** Yemek kartı modülü açık mı. */
  mealOn?: boolean
  onCancel: () => void
  /** Present only for an owner; see DiscountPicker. */
  onSaveRates?: (next: number[]) => Promise<void>
}

/**
 * Splits one table's bill into pieces paid for separately.
 *
 * Three panels: unpaid lines on the left, this person's share in the
 * middle, and the amount due, plus payment, on the right. Tapping a line
 * moves one unit at a time, in either direction, the same gesture whether
 * building up a share or changing your mind about an item in it.
 *
 * Stays mounted across parts rather than closing after each one: `ticket` is
 * the live open ticket (SalePage's `editing`, sourced from the open-tickets
 * subscription), so once a part is settled the prop simply arrives smaller
 * and the panels reflect it, the common case of several people paying in a
 * row costs one tap each instead of reopening this sheet every time.
 */
export function SplitPaymentSheet({
  ticket,
  discounts,
  onSettlePart,
  onSettleAmount,
  partials = [],
  onCancel,
  onSaveRates,
  bahsisLabel,
  cariOn,
  mealOn,
}: Props) {
  /** lineKey → quantity moved to the middle panel. */
  const [taken, setTaken] = useState<Map<string, number>>(new Map())
  /** Alınan ara ödemeler (bu adisyonda) ve kalan. */
  const prepaidKurus = ticket.prepaidKurus ?? 0
  const dueKurus = Math.max(0, ticket.totalKurus - prepaidKurus)
  /**
   * Ürünle (bugünkü) ya da Tutarla. Ara ödeme alınmış adisyonda ürünle bölme
   * yok: ürün fiyatları kalanla birlikte hesabı bozar, kalan tutarla sürer.
   */
  const [mode, setMode] = useState<'items' | 'amount'>(prepaidKurus > 0 ? 'amount' : 'items')
  /** Tutarla: yazılan tutar (lira, metin). */
  const [amountText, setAmountText] = useState('')
  const amountKurus = Math.min(liraToKurus(amountText) ?? 0, dueKurus)
  const amountMode = mode === 'amount'
  /**
   * Telefonda Ürünle iki kart (kullanıcı, 22 Eki 2026): 1. "Adisyonda
   * kalan", 2. "Ödenecek"; oklarla geçiliyor. Masaüstünde ikisi yan yana.
   */
  const [page, setPage] = useState<'left' | 'middle'>('left')
  const [discounting, setDiscounting] = useState(false)
  /** Ödeme düğmelerinin altında açık panel (PayActions). */
  const [panel, setPanel] = useState<PayPanel>('none')

  /**
   * Görünen ekran yüksekliği (26 Eki 2026): telefonda nakit tutarı yazarken
   * klavye açılıyor, pencere ise tam ekran boyunda kalıp "Tamam" klavyenin
   * altında kalıyordu. Pencere görünen alana sığdırılıyor.
   */
  const [viewH, setViewH] = useState<number | null>(null)
  useEffect(() => {
    const vv = window.visualViewport
    if (!vv) return
    const on = () => setViewH(vv.height < window.innerHeight - 80 ? vv.height : null)
    vv.addEventListener('resize', on)
    on()
    return () => vv.removeEventListener('resize', on)
  }, [])
  const [tenderPrint, setTenderPrint] = useState(false)
  /** Cari hesap seçme penceresi açık mı. */
  const [pickingDebtor, setPickingDebtor] = useState(false)

  const leftLines = useMemo(() => {
    return ticket.lines
      .map((line) => {
        const took = taken.get(lineKey(line)) ?? 0
        const qty = line.qty - took
        return qty > 0 ? withQty(line, qty, 'end') : null
      })
      .filter((l): l is OrderLine => l !== null)
  }, [ticket.lines, taken])

  const middleLines = useMemo(() => {
    return ticket.lines
      .map((line) => {
        const took = taken.get(lineKey(line)) ?? 0
        return took > 0
          ? withQty(line, took)
          : null
      })
      .filter((l): l is OrderLine => l !== null)
  }, [ticket.lines, taken])

  const middleTotalKurus = useMemo(
    () => middleLines.reduce((sum, l) => sum + l.lineTotalKurus, 0),
    [middleLines],
  )

  const leftTotalKurus = useMemo(
    () => leftLines.reduce((sum, l) => sum + l.lineTotalKurus, 0),
    [leftLines],
  )

  function take(line: OrderLine) {
    const key = lineKey(line)
    setTaken((prev) => {
      const next = new Map(prev)
      const current = next.get(key) ?? 0
      // The left panel already shows what remains, so a click here can never
      // ask for more than is left to take.
      next.set(key, current + 1)
      return next
    })
  }

  function giveBack(line: OrderLine) {
    const key = lineKey(line)
    setTaken((prev) => {
      const next = new Map(prev)
      const current = next.get(key) ?? 0
      if (current <= 1) next.delete(key)
      else next.set(key, current - 1)
      return next
    })
  }

  function settle(method: PaymentMethod, extras: SplitExtras, print: boolean) {
    if (amountMode) {
      onSettleAmount(amountKurus, method, extras, print)
      setAmountText('')
      setPanel('none')
      setPickingDebtor(false)
      return
    }
    // A fresh snapshot: middleLines already reflects the current selection,
    // and onSettlePart shrinks `ticket` for the next part rather than closing
    // this sheet, so the selection is cleared for whoever pays next.
    onSettlePart(middleLines, method, extras, print)
    setTaken(new Map())
    setPage('left')
    setDiscounting(false)
    setPanel('none')
    setPickingDebtor(false)
  }

  // The discount step replaces the three panels rather than nesting inside
  // them: DiscountPicker renders its own .dlg__card, and putting that inside
  // the split card's grid left it stranded in a corner of a very wide box.
  if (discounting) {
    return (
      <div className="dlg" role="dialog" aria-modal="true">
        <DiscountTender
          totalKurus={middleTotalKurus}
          discounts={discounts}
          onCancel={() => setDiscounting(false)}
          onSaveRates={onSaveRates}
          bahsisLabel={bahsisLabel}
          onConfirm={(method, percent, print, bahsis) =>
            // Parça açık masadan: kendi kuralında fiş basılmıyor.
            settle(method, { discountPercent: percent, ...bahsis }, print ?? false)
          }
        />
      </div>
    )
  }

  const tabs = (
    <div className="split__tabs" role="tablist" aria-label="Bölme şekli">
      <button
        type="button"
        role="tab"
        aria-selected={!amountMode}
        className={`split__tab--items ${!amountMode ? 'is-on' : ''}`}
        disabled={prepaidKurus > 0}
        title={prepaidKurus > 0 ? 'Ara ödeme alınmış adisyon kalan tutarla bölünür' : undefined}
        onClick={() => setMode('items')}
      >
        Ürünle
      </button>
      <button
        type="button"
        role="tab"
        aria-selected={amountMode}
        className={`split__tab--amount ${amountMode ? 'is-on' : ''}`}
        onClick={() => {
          setMode('amount')
          setTaken(new Map())
          setPanel('none')
        }}
      >
        Tutarla
      </button>
    </div>
  )

  return (
    <div className={`dlg ${viewH ? 'dlg--kb' : ''}`} role="dialog" aria-modal="true">
      {/* Kip rengi (22 Eki 2026): Ürünle mavi, Tutarla mor; sekme, ödeme
          sütununun şeridi ve "Bu pay" kutusu bu renkte. */}
      <div
        className={`dlg__card dlg__card--split ${amountMode ? 'is-amount' : 'is-items'} page-${page} ${panel !== 'none' ? `has-panel panel-${panel}` : ''}`}
        style={viewH ? ({ '--view-h': `${viewH}px` } as CSSProperties) : undefined}
      >
        {/* Telefonda başlık ve sekmeler en üstte, çarpıyla aynı satırda;
            masaüstünde sekmeler sol sütunda. */}
        <div className="split__top">
          <h2 className="split__title">Parçalı Öde</h2>
          {tabs}
        </div>

        {amountMode ? (
          <>
        <div className="split__panel split__panel--left">
          {tabs}
          <h3>Adisyon</h3>
          <dl className="split__due">
            <div>
              <dt>Toplam</dt>
              <dd>{formatTRY(ticket.totalKurus)}</dd>
            </div>
            {/* Tek tek: ilkinde kaç, ikincide kaç (22 Eki 2026). */}
            {partials.map((o, i) => (
              <div key={o.id} className="is-paid">
                <dt>
                  {i + 1}. ara ödeme
                  {o.paymentMethod && <small>, {PAYMENT_LABELS[o.paymentMethod]}</small>}
                </dt>
                <dd>−{formatTRY(o.totalKurus)}</dd>
              </div>
            ))}
            {prepaidKurus - partials.reduce((s, o) => s + o.totalKurus, 0) > 0 && (
              <div className="is-paid">
                <dt>Önceki ara ödemeler</dt>
                <dd>−{formatTRY(prepaidKurus - partials.reduce((s, o) => s + o.totalKurus, 0))}</dd>
              </div>
            )}
            <div className="is-due">
              <dt>Kalan</dt>
              <dd>{formatTRY(dueKurus)}</dd>
            </div>
          </dl>
          <p className="split__hint">
            Ürün seçmeden tutar alınır, masa açık kalır. Kalanın tamamı alınınca adisyon kapanır.
          </p>
        </div>

        <div className="split__panel split__panel--middle">
          <h3 className="split__amount-title">Ödenecek tutar</h3>
          <AmountField
            size="lg"
            value={amountText}
            onChange={(t) => {
              // Kalandan fazlası yazılırsa kalanın tamamına düşüyor; uyarı
              // yazısı yok (kullanıcı, 22 Eki 2026).
              const k = liraToKurus(t)
              setAmountText(k !== null && k > dueKurus ? kurusToLiraInput(dueKurus) : t)
            }}
            ariaLabel="Ödenecek tutar"
          />
          {/* Kısayollar: yarısı, kişi sayısına bölme, kalanın tamamı. */}
          <div className="split__quick">
            {[2, 3, 4].map((n) => (
              <button
                key={n}
                type="button"
                onClick={() => setAmountText(kurusToLiraInput(Math.ceil(dueKurus / n)))}
              >
                {n === 2 ? 'Yarısı' : `${n}'e böl`}
                <small>{formatTRY(Math.ceil(dueKurus / n))}</small>
              </button>
            ))}
            <button type="button" className="is-all" onClick={() => setAmountText(kurusToLiraInput(dueKurus))}>
              Kalanın tamamı
              <small>{formatTRY(dueKurus)}</small>
            </button>
          </div>
        </div>

          </>
        ) : (
          <>
        <div className="split__panel split__panel--left">
          {tabs}
          <div className="split__pager">
            <button type="button" disabled aria-label="Önceki">
              <ChevronLeftIcon />
            </button>
            <span>
              <b>Adisyonda kalan</b>
              <small>{formatTRY(leftTotalKurus)}</small>
            </span>
            <button type="button" onClick={() => setPage('middle')} aria-label="Ödenecek">
              {middleLines.length > 0 && (
                <i className="split__pager-count">{middleLines.reduce((n, l) => n + l.qty, 0)}</i>
              )}
              <ChevronRightIcon />
            </button>
          </div>
          <h3>
            Adisyonda kalan
            <span className="split__panel-sum">{formatTRY(leftTotalKurus)}</span>
          </h3>
          {leftLines.length === 0 ? (
            <p className="split__empty">Bütün ürünler seçildi.</p>
          ) : (
            <ul className="split__lines">
              {leftLines.map((line) => (
                <li key={lineKey(line)}>
                  <button type="button" className="split__line" onClick={() => take(line)}>
                    <span className="split__line-qty">{line.qty}×</span>
                    <span className="split__line-name">
                      {line.name}
                      {line.note && <em className="split__line-note"> ({line.note})</em>}
                    </span>
                    <span className="split__line-total">{formatTRY(line.lineTotalKurus)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="split__panel split__panel--middle">
          <div className="split__pager">
            <button type="button" onClick={() => setPage('left')} aria-label="Adisyonda kalan">
              <ChevronLeftIcon />
            </button>
            <span>
              <b>Ödenecek</b>
              <small>{formatTRY(middleTotalKurus)}</small>
            </span>
            <button type="button" disabled aria-label="Sonraki">
              <ChevronRightIcon />
            </button>
          </div>
          <h3>
            Bu kişinin payı
            <span className="split__panel-sum">{formatTRY(middleTotalKurus)}</span>
          </h3>
          {middleLines.length === 0 ? (
            <p className="split__empty">Soldan ürün seçin.</p>
          ) : (
            <ul className="split__lines">
              {middleLines.map((line) => (
                <li key={lineKey(line)}>
                  <button
                    type="button"
                    className="split__line split__line--taken"
                    onClick={() => giveBack(line)}
                    title="Geri koy"
                  >
                    <span className="split__line-qty">{line.qty}×</span>
                    <span className="split__line-name">
                      {line.name}
                      {line.note && <em className="split__line-note"> ({line.note})</em>}
                    </span>
                    <span className="split__line-total">{formatTRY(line.lineTotalKurus)}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>

          </>
        )}

        <div className="split__panel split__panel--pay">
          <div className="split__total">
            <span>{amountMode && amountKurus >= dueKurus && amountKurus > 0 ? 'Kalanın tamamı' : 'Bu pay'}</span>
            <strong>{formatTRY(amountMode ? amountKurus : middleTotalKurus)}</strong>
          </div>

          {/* Kasadaki ödeme alanının aynısı (kullanıcı, 21 Eki 2026), yalnız
              Parçalı Öde düğmesi yok: her kişi kendi payını istediği yöntemle
              (nakit, kart, cari, yemek kartı) ödüyor. Fiş basılmıyor; parça
              açık masadan, garsonun fişi zaten basıldı. */}
          <PayActions
            totalKurus={amountMode ? amountKurus : middleTotalKurus}
            disabled={amountMode ? amountKurus <= 0 : middleLines.length === 0}
            panel={panel}
            onPanel={setPanel}
            tenderPrint={tenderPrint}
            onTender={(print) => {
              setTenderPrint(print)
              setPanel('tender')
            }}
            // Ara ödemede indirim yok: indirim kapanışta kalana uygulanıyor.
            onDiscount={amountMode ? undefined : () => setDiscounting(true)}
            onPay={(method, extras, print) => settle(method, extras, print ?? false)}
            bahsisLabel={bahsisLabel}
            onDebtor={cariOn ? () => setPickingDebtor(true) : undefined}
            mealOn={mealOn}
          />

          {pickingDebtor && (
            <DebtorPicker
              totalKurus={amountMode ? amountKurus : middleTotalKurus}
              onCancel={() => setPickingDebtor(false)}
              onPick={(debtor, note) => settle('credit', { debtor: { debtor, note } }, false)}
            />
          )}
        </div>

        {/* Pinned to the card's own corner rather than the bottom of the pay
            column: it is a way out of the whole sheet, not one of that
            column's three actions, and at the foot of the buttons it read as
            a fourth, the one that happens to be nearest a thumb once the
            other three are disabled. */}
        <button
          type="button"
          className="split__x"
          onClick={onCancel}
          title="Kapat"
          aria-label="Kapat"
        >
          <XIcon />
        </button>
      </div>
    </div>
  )
}

